// SmartFindBar — intelligent bingo router.
//
// User types free-text ("rekensom groep 5", "kerstborrel kantoor", "80s foute
// muziek") → we match against keyword patterns and route directly to the right
// landing. If nothing matches, we fall back to the AI generator with the query
// pre-loaded as prompt.
//
// Design: single big input + 6 quick-chips below + live-match dropdown.
// Fully client-side. Zero latency. Zero LLM cost for the 90% common cases.
//
// Added: 2026-02-28 per user request "intelligent zoeken en invullen".

import { useState, useRef, useEffect, useMemo } from "react";
import { Search, ArrowRight, Sparkles } from "lucide-react";
import { useI18n } from "../lib/i18n";

/* ═══════════════════════════════════════════════════════════════════════════
   Route patterns (per language). Order matters: first match wins.
   ═══════════════════════════════════════════════════════════════════════════ */
const ROUTE_PATTERNS = {
  nl: [
    { rx: /\b(reken|som|tafel|getall?en\s*groep|spelling|taalles|klas|leraar|leraren|school|woordjes|letters|groep\s*\d)\b/i,
      href: "/nl/bingo-voor-leraren/", emoji: "🍎", label: "Voor leraren — bingo voor de klas" },
    { rx: /\b(spotify|web[-\s]?playback|premium)\b/i,
      href: "/nl/muziek-bingo/#spotify", emoji: "🎧", label: "Muziekbingo met Spotify" },
    { rx: /\bfoute?\s*kerst\b/i,
      href: "/nl/muziek-bingo/foute-kerst-hits/", emoji: "🎄", label: "Foute kerst muziekbingo" },
    { rx: /\bfoute?\s*(bruiloft|trouw)\b/i,
      href: "/nl/muziek-bingo/foute-hits-bruiloft/", emoji: "💍", label: "Foute bruiloft muziekbingo" },
    { rx: /\bfoute?\s*(bedrijf|kantoor|personeel|firma)\b/i,
      href: "/nl/muziek-bingo/foute-hits-bedrijfsfeest/", emoji: "🥂", label: "Foute bedrijfsfeest muziekbingo" },
    { rx: /\b(nederlandstalig|hollands?|meezing)\b/i,
      href: "/nl/muziek-bingo/nederlandstalig/", emoji: "🇳🇱", label: "Nederlandstalige muziekbingo" },
    { rx: /\b(muziek|song|artiest|band|hits?|nummers?|80s?|90s?|00s?|jaren\s*(60|70|80|90|00|10)|rock|pop|schlager)\b/i,
      href: "/nl/muziek-bingo/", emoji: "🎵", label: "Muziekbingo (14 genres)" },
    { rx: /\b(ballen\s*trekker|balletjes|ballentrekker|caller|bingomolen|molen|ballen)\b/i,
      href: "/caller", emoji: "🎱", label: "Bingo ballen trekker" },
    { rx: /\b(nummer\s*bingo|nummerbingo|getall?en\s*bingo|cijferbingo|1[-\s]?75|1[-\s]?90|75|90|traditioneel|klassiek(e)?\s*bingo)\b/i,
      href: "/create?numbers=75", emoji: "🎲", label: "Nummerbingo 75/90 — kant-en-klare kaarten" },
    { rx: /\b(sinterklaas|sint|piet|pakjes|schoenzetten)\b/i,
      href: "/sinterklaas-bingo/", emoji: "🎁", label: "Sinterklaas Bingo" },
    { rx: /\b(kerst|kerstborrel|kerstdiner|kerstfeest|santa)\b/i,
      href: "/nl/kerstbingo/", emoji: "🎄", label: "Kerstbingo" },
    { rx: /\b(halloween|griezel|spook|pompoen)\b/i,
      href: "/nl/halloweenbingo/", emoji: "🎃", label: "Halloween Bingo" },
    { rx: /\b(bruiloft|trouwen|huwelijk|wedding)\b/i,
      href: "/nl/bruiloftsbingo/", emoji: "💍", label: "Bruiloftsbingo" },
    { rx: /\b(vrijgezel|jga|bachelorette|vrijges)\b/i,
      href: "/nl/vrijgezellenfeest/", emoji: "🎉", label: "Vrijgezellenfeest bingo" },
    { rx: /\b(bedrijfsfeest|kantoorfeest|personeelsfeest|firmafeest|team\s*bingo)\b/i,
      href: "/nl/bedrijfsfeestbingo/", emoji: "🥂", label: "Bedrijfsfeest bingo" },
    { rx: /\b(verjaardag|jarig|birthday)\b/i,
      href: "/nl/verjaardagsbingo/", emoji: "🎂", label: "Verjaardagsbingo" },
    { rx: /\b(kinderfeestje|kinderpartij|kids|kind)\b/i,
      href: "/nl/kinderfeestje/", emoji: "🧸", label: "Kinderfeestje bingo" },
    { rx: /\b(zelf|eigen|upload|foto|afbeelding|custom)\b/i,
      href: "/create?blank=1", emoji: "✏️", label: "Eigen bingo maken (upload)" },
  ],
  en: [
    { rx: /\b(spotify|web[-\s]?playback|premium)\b/i,
      href: "/en/music-bingo/#spotify", emoji: "🎧", label: "Music bingo with Spotify" },
    { rx: /\b(music|song|artist|band|hits?|80s?|90s?|00s?|rock|pop)\b/i,
      href: "/en/music-bingo/", emoji: "🎵", label: "Music bingo (14 genres)" },
    { rx: /\b(balls?|caller|ball\s*drawer|draw\s*machine|bingo\s*cage)\b/i,
      href: "/caller", emoji: "🎱", label: "Bingo ball caller" },
    { rx: /\b(number\s*bingo|1[-\s]?75|1[-\s]?90|75|90|traditional|classic\s*bingo)\b/i,
      href: "/create?numbers=75", emoji: "🎲", label: "Number bingo 75/90 — ready-made cards" },
    { rx: /\b(christmas|xmas|santa)\b/i,
      href: "/en/christmas-bingo/", emoji: "🎄", label: "Christmas Bingo" },
    { rx: /\b(halloween|spooky|ghost|pumpkin)\b/i,
      href: "/en/halloween-bingo/", emoji: "🎃", label: "Halloween Bingo" },
    { rx: /\b(wedding|marry|marriage|bride)\b/i,
      href: "/en/wedding-bingo/", emoji: "💍", label: "Wedding Bingo" },
    { rx: /\b(birthday|bday)\b/i,
      href: "/en/birthday-bingo/", emoji: "🎂", label: "Birthday Bingo" },
    { rx: /\b(bachelorette|hen\s*party|bachelor)\b/i,
      href: "/en/bachelorette-bingo/", emoji: "🎉", label: "Bachelorette bingo" },
    { rx: /\b(school|teacher|classroom|math|spelling|kids?)\b/i,
      href: "/en/", emoji: "🍎", label: "School / classroom bingo" },
    { rx: /\b(own|custom|upload|photo|image)\b/i,
      href: "/create?blank=1", emoji: "✏️", label: "Custom bingo (upload)" },
  ],
  de: [
    { rx: /\b(spotify|web[-\s]?playback|premium)\b/i,
      href: "/de/musik-bingo/#spotify", emoji: "🎧", label: "Musikbingo mit Spotify" },
    { rx: /\b(musik|song|künstler|band|hits?|80er|90er|00er|rock|pop|schlager)\b/i,
      href: "/de/musik-bingo/", emoji: "🎵", label: "Musikbingo (14 Genres)" },
    { rx: /\b(kugeln|kugelzieher|caller|ziehmaschine)\b/i,
      href: "/caller", emoji: "🎱", label: "Bingo-Kugelzieher" },
    { rx: /\b(zahlenbingo|zahlen\s*bingo|1[-\s]?75|1[-\s]?90|75|90|klassisch|traditionell)\b/i,
      href: "/create?numbers=75", emoji: "🎲", label: "Zahlenbingo 75/90 — fertige Karten" },
    { rx: /\b(weihnach|xmas|nikolaus|santa)\b/i,
      href: "/de/weihnachtsbingo/", emoji: "🎄", label: "Weihnachtsbingo" },
    { rx: /\b(halloween|geist|kürbis)\b/i,
      href: "/de/halloween-bingo/", emoji: "🎃", label: "Halloween Bingo" },
    { rx: /\b(hochzeit|braut|heirat)\b/i,
      href: "/de/hochzeitsbingo/", emoji: "💍", label: "Hochzeitsbingo" },
    { rx: /\b(geburtstag)\b/i,
      href: "/de/geburtstagsbingo/", emoji: "🎂", label: "Geburtstagsbingo" },
    { rx: /\b(junggesell|polterabend)\b/i,
      href: "/de/junggesellinnenabschied/", emoji: "🎉", label: "Junggesellinnenabschied Bingo" },
    { rx: /\b(schule|lehrer|klasse|mathe|kinder)\b/i,
      href: "/de/", emoji: "🍎", label: "Schul-Bingo" },
    { rx: /\b(eigen|selbst|upload|foto)\b/i,
      href: "/create?blank=1", emoji: "✏️", label: "Eigenes Bingo (Upload)" },
  ],
};

/* Quick-chip prompts shown by default (i18n label + query it fires) */
const QUICK_CHIPS = {
  nl: [
    { label: "🎸 Nummers van Aerosmith", q: "muziekbingo met nummers van Aerosmith" },
    { label: "🎄 Kerstborrel", q: "kerstborrel" },
    { label: "🎧 80s muziek Spotify", q: "80s muziek spotify" },
    { label: "🎂 Verjaardag 40", q: "verjaardag 40 jaar" },
    { label: "🍎 Rekenen groep 5", q: "rekenen groep 5" },
    { label: "✨ Iets bijzonders...", q: "" },
  ],
  en: [
    { label: "🎄 Christmas party", q: "christmas party" },
    { label: "🎧 80s Spotify music", q: "80s music spotify" },
    { label: "🎂 40th birthday", q: "40th birthday" },
    { label: "🍎 Classroom bingo", q: "classroom bingo" },
    { label: "💍 Wedding music", q: "wedding music" },
    { label: "✨ Something else...", q: "" },
  ],
  de: [
    { label: "🎄 Weihnachtsfeier", q: "weihnachtsfeier" },
    { label: "🎧 80er Spotify", q: "80er musik spotify" },
    { label: "🎂 40. Geburtstag", q: "40 geburtstag" },
    { label: "🍎 Klassenzimmer", q: "klassenzimmer bingo" },
    { label: "💍 Hochzeitsmusik", q: "hochzeit musik" },
    { label: "✨ Etwas anderes...", q: "" },
  ],
};

const COPY = {
  nl: {
    heading: "Welke bingo wil je maken?",
    sub: "Typ waar je bingo over gaat — we vullen je kaarten meteen voor of sturen je naar de juiste generator.",
    placeholder: "bv. 'muziekbingo met nummers van Aerosmith' of 'rekenen groep 5'…",
    go: "Ga",
    fallbackAI: "Laat AI een bingo maken over",
    aiPrimary: "AI vult je kaarten meteen voor",
    match: "Ga naar",
    popular: "Populair om te maken",
    other: "Of ga direct naar",
  },
  en: {
    heading: "Which bingo do you want?",
    sub: "Type what your bingo is about — we pre-fill your cards instantly or route you to the right generator.",
    placeholder: "e.g. 'music bingo with Aerosmith songs' or 'classroom math'…",
    go: "Go",
    fallbackAI: "Have AI create a bingo about",
    aiPrimary: "AI fills your cards instantly",
    match: "Go to",
    popular: "Popular to create",
    other: "Or go straight to",
  },
  de: {
    heading: "Welches Bingo willst du machen?",
    sub: "Tippe worum es geht — wir füllen deine Karten sofort vor oder leiten dich zum richtigen Generator.",
    placeholder: "z.B. 'Musikbingo mit Songs von Aerosmith' oder 'Mathe Klasse 5'…",
    go: "Los",
    fallbackAI: "KI ein Bingo erstellen lassen über",
    aiPrimary: "KI füllt deine Karten sofort",
    match: "Zu",
    popular: "Beliebt zum Erstellen",
    other: "Oder direkt zu",
  },
};

/* Popular default suggestions shown the moment the field is focused (empty
   query) — gives users an instant sense of what they can search for. */
const POPULAR = {
  nl: ["muziek-bingo/#spotify", "muziek-bingo/", "bingo-voor-leraren/", "kerstbingo/", "verjaardagsbingo/", "caller"],
  en: ["music-bingo/#spotify", "music-bingo/", "christmas-bingo/", "birthday-bingo/", "caller"],
  de: ["musik-bingo/#spotify", "musik-bingo/", "weihnachtsbingo/", "geburtstagsbingo/", "caller"],
};

/** Return the first pattern that matches `q`, or null. */
function findRoute(q, lang) {
  const patterns = ROUTE_PATTERNS[lang] || ROUTE_PATTERNS.nl;
  const clean = (q || "").trim();
  if (!clean) return null;
  for (const p of patterns) {
    if (p.rx.test(clean)) return p;
  }
  return null;
}

/** Return ALL patterns that match `q` (order = priority), max `limit`. */
function findRoutes(q, lang, limit = 5) {
  const patterns = ROUTE_PATTERNS[lang] || ROUTE_PATTERNS.nl;
  const clean = (q || "").trim();
  if (!clean) return [];
  const out = [];
  const seen = new Set();
  for (const p of patterns) {
    if (p.rx.test(clean) && !seen.has(p.href)) {
      out.push(p);
      seen.add(p.href);
      if (out.length >= limit) break;
    }
  }
  return out;
}

/** Popular default routes (resolved from POPULAR hrefs → full pattern objs). */
function popularRoutes(lang) {
  const patterns = ROUTE_PATTERNS[lang] || ROUTE_PATTERNS.nl;
  const wanted = POPULAR[lang] || POPULAR.nl;
  const out = [];
  for (const key of wanted) {
    const full = `/${lang}/${key}`;
    const p = patterns.find((x) => x.href === full || x.href.endsWith(key));
    if (p) out.push(p);
  }
  return out;
}

/* Words that signal the user is describing a SPECIFIC bingo they want built —
   e.g. "maak een muziekbingo met alle nummers van Aerosmith". When detected we
   skip the generic hub routing and send them straight to AI auto-generation. */
const INTENT_RX = /\b(maak|make|genereer|generate|erstell\w*|mach\w*|met\s+\w+\s+erin|nummers?\s+van|liedjes?\s+van|songs?\s+(van|by|of)|hits?\s+van|muziek\s+van|over|about|über|thema|theme|met\s+de\s+nummers)\b/i;

/** True when the query looks like a specific "build me X" request. */
function isGenerateIntent(q) {
  const clean = (q || "").trim();
  if (clean.length < 3) return false;
  const words = clean.split(/\s+/).length;
  if (INTENT_RX.test(clean)) return true;   // explicit build/topic language
  if (words >= 5) return true;              // a full descriptive sentence
  return false;
}

/** Build the AI auto-generate URL — lands directly in the React Builder and
    auto-generates + pre-fills the cards (no extra clicks). */
function aiUrl(q, lang) {
  return `/create?ai=1&autogen=1&prompt=${encodeURIComponent(q.trim())}`;
}

export default function SmartFindBar() {
  const { t, lang } = useI18n();
  const copy = COPY[lang] || COPY.nl;
  const chips = QUICK_CHIPS[lang] || QUICK_CHIPS.nl;
  const [q, setQ] = useState("");
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);

  const match = useMemo(() => findRoute(q, lang), [q, lang]);
  const matches = useMemo(() => findRoutes(q, lang), [q, lang]);
  const popular = useMemo(() => popularRoutes(lang), [lang]);
  const genIntent = useMemo(() => isGenerateIntent(q), [q]);
  const hasQuery = q.trim().length >= 2;
  const showSuggestion = focused && (hasQuery || q.trim().length === 0);

  function go() {
    const clean = q.trim();
    if (!clean) return;
    // Specific "build me X" request → straight to AI auto-generation.
    if (isGenerateIntent(clean)) {
      window.location.href = aiUrl(clean, lang);
      return;
    }
    const m = findRoute(clean, lang);
    if (m) {
      window.location.href = m.href;
    } else {
      window.location.href = aiUrl(clean, lang);
    }
  }

  function onKey(e) {
    if (e.key === "Enter") { e.preventDefault(); go(); }
  }

  function pickChip(chip) {
    if (!chip.q) { inputRef.current?.focus(); return; }
    setQ(chip.q);
    if (isGenerateIntent(chip.q)) { window.location.href = aiUrl(chip.q, lang); return; }
    const m = findRoute(chip.q, lang);
    window.location.href = m ? m.href : aiUrl(chip.q, lang);
  }

  return (
    <section className="border-b-2 border-black bg-[var(--brand-yellow)]" data-testid="smart-find-bar">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-10 sm:py-14">
        <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-center leading-tight">
          {copy.heading}
        </h2>
        <p className="text-center text-neutral-800 mt-2 mb-6 text-sm sm:text-base font-medium max-w-xl mx-auto">
          {copy.sub}
        </p>

        {/* Big input row */}
        <div className="relative">
          <div className="flex items-stretch nb-border bg-white rounded-lg overflow-hidden nb-shadow-lg">
            <div className="pl-3 sm:pl-4 flex items-center">
              <Search size={20} className="text-neutral-500" />
            </div>
            <input
              ref={inputRef}
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setTimeout(() => setFocused(false), 200)}
              onKeyDown={onKey}
              placeholder={copy.placeholder}
              className="flex-1 px-3 py-4 sm:py-5 text-base sm:text-lg font-medium focus:outline-none bg-white"
              data-testid="smart-find-input"
            />
            <button
              onClick={go}
              className="bg-[var(--brand-red)] text-white px-4 sm:px-8 font-display font-black text-base sm:text-lg flex items-center gap-2 hover:brightness-110 transition-all"
              data-testid="smart-find-submit"
            >
              {copy.go} <ArrowRight size={20} />
            </button>
          </div>

          {/* Live-match suggestions dropdown */}
          {showSuggestion && (
            <div className="absolute left-0 right-0 top-full mt-2 nb-border bg-white rounded-lg nb-shadow-lg z-30 overflow-hidden" data-testid="smart-find-suggestions">
              {!hasQuery ? (
                <>
                  <div className="px-4 pt-3 pb-1 text-[10px] font-black uppercase tracking-widest text-neutral-500">{copy.popular}</div>
                  {popular.map((p, i) => (
                    <a
                      key={p.href}
                      href={p.href}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-[var(--brand-yellow)] transition-colors border-t border-neutral-100"
                      data-testid={`smart-find-popular-${i}`}
                    >
                      <span className="text-2xl">{p.emoji}</span>
                      <div className="flex-1 text-base font-black text-black">{p.label}</div>
                      <ArrowRight size={18} className="text-[var(--brand-red)]" />
                    </a>
                  ))}
                </>
              ) : (
                <>
                  {/* Primary AI-generate option (shown first when the query is a
                      specific "build me X" request, else shown at the bottom) */}
                  {genIntent && (
                    <a
                      href={aiUrl(q, lang)}
                      className="flex items-center gap-3 px-4 py-3.5 bg-[var(--brand-red)] text-white hover:brightness-110 transition-all"
                      data-testid="smart-find-ai-primary"
                    >
                      <span className="text-2xl"><Sparkles size={24} /></span>
                      <div className="flex-1">
                        <div className="text-[10px] font-black uppercase tracking-widest text-white/80">{copy.aiPrimary}</div>
                        <div className="text-base font-black">"{q}"</div>
                      </div>
                      <ArrowRight size={18} className="text-white" />
                    </a>
                  )}

                  {matches.length > 0 && (
                    <>
                      {genIntent && (
                        <div className="px-4 pt-3 pb-1 text-[10px] font-black uppercase tracking-widest text-neutral-500 border-t border-neutral-100">{copy.other}</div>
                      )}
                      {matches.map((m, i) => (
                        <a
                          key={m.href}
                          href={m.href}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-[var(--brand-yellow)] transition-colors border-t border-neutral-100 first:border-t-0"
                          data-testid={i === 0 ? "smart-find-match" : `smart-find-match-${i}`}
                        >
                          <span className="text-2xl">{m.emoji}</span>
                          <div className="flex-1">
                            {i === 0 && !genIntent && <div className="text-[10px] font-black uppercase tracking-widest text-neutral-500">{copy.match}</div>}
                            <div className="text-base font-black text-black">{m.label}</div>
                          </div>
                          <ArrowRight size={18} className="text-[var(--brand-red)]" />
                        </a>
                      ))}
                    </>
                  )}

                  {/* AI fallback at the bottom when NOT a specific request */}
                  {!genIntent && (
                    <a
                      href={aiUrl(q, lang)}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-[var(--brand-yellow)] transition-colors border-t-2 border-black bg-neutral-50"
                      data-testid="smart-find-fallback"
                    >
                      <span className="text-2xl"><Sparkles size={24} /></span>
                      <div className="flex-1">
                        <div className="text-[10px] font-black uppercase tracking-widest text-neutral-500">{copy.fallbackAI}</div>
                        <div className="text-base font-black text-black">"{q}"</div>
                      </div>
                      <ArrowRight size={18} className="text-[var(--brand-red)]" />
                    </a>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Quick-pick chips */}
        <div className="flex flex-wrap gap-2 justify-center mt-5" data-testid="smart-find-chips">
          {chips.map((c, i) => (
            <button
              key={i}
              onClick={() => pickChip(c)}
              className="text-xs sm:text-sm font-bold bg-white nb-border rounded-full px-3 py-1.5 hover:bg-black hover:text-[var(--brand-yellow)] transition-colors"
              data-testid={`smart-find-chip-${i}`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
